// Attack Path, the only mode that still uses Cytoscape, and only when
// there's a real attack subgraph to draw. each attack_run becomes a red
// edge from a synthetic Attacker node to the target host. the host data
// payload doubles as the drawer source so clicking either side opens
// useful details.
//
// empty-state: clean "No attack runs captured for this engagement."
// overlay. the canvas stays mounted so re-entering the mode after data
// arrives works without a remount

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import cytoscape, { Core } from 'cytoscape'
import type {
  EngagementAsset,
  EngagementAttackRun,
  TopologySnapshot,
} from '@/lib/engagementsApi'
import { groupAttackRuns } from './topologyAggregates'

export interface AttackPathCanvasHandle {
  zoomIn:  () => void
  zoomOut: () => void
  fit:     () => void
}

interface Props {
  assets:     EngagementAsset[]
  attackRuns: EngagementAttackRun[]
  snapshot:   TopologySnapshot | null
  onSelect:   (data: Record<string, any> | null) => void
}

const FIT_PADDING = 60

const styles: any[] = [
  {
    selector: 'node',
    style: {
      'label':            'data(label)',
      'font-size':        14,
      'color':            '#0f172a',
      'text-valign':      'bottom',
      'text-halign':      'center',
      'text-margin-y':    6,
      'text-wrap':        'wrap',
      'text-max-width':   180,
      'text-background-color': '#ffffff',
      'text-background-opacity': 0.95,
      'text-background-padding': 4,
      'background-color': '#e2e8f0',
      'border-color':     '#475569',
      'border-width':     2,
      'width':            70,
      'height':           70,
    },
  },
  {
    selector: 'node[type="Attacker"]',
    style: {
      'background-color': '#1e293b',
      'border-color':     '#475569',
      'shape':            'round-rectangle',
      'width':            120,
      'height':           80,
      'color':            '#f8fafc',
      'text-valign':      'center',
      'text-halign':      'center',
      'text-background-opacity': 0,
      'font-weight':      'bold',
    },
  },
  {
    selector: 'node[type="Host"]',
    style: {
      'shape':            'ellipse',
      'background-color': '#fee2e2',
      'border-color':     '#dc2626',
      'border-width':     3,
      'width':            56,
      'height':           56,
      'font-size':        12,
      'text-max-width':   160,
    },
  },
  {
    selector: 'edge',
    style: {
      'width':              3,
      'line-color':         '#dc2626',
      'target-arrow-color': '#dc2626',
      'target-arrow-shape': 'triangle',
      'curve-style':        'bezier',
      'label':              'data(label)',
      'font-size':          11,
      'color':              '#7f1d1d',
      'text-background-color': '#ffffff',
      'text-background-opacity': 0.95,
      'text-background-padding': 3,
      'text-rotation':      'autorotate',
    },
  },
  {
    selector: 'node:selected, edge:selected',
    style: {
      'border-color': '#2563eb',
      'line-color':   '#2563eb',
      'target-arrow-color': '#2563eb',
    },
  },
]

export const AttackPathCanvas = forwardRef<AttackPathCanvasHandle, Props>(
  ({ assets, attackRuns, snapshot: _snapshot, onSelect }, ref) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const cyRef        = useRef<Core | null>(null)

    useImperativeHandle(ref, () => ({
      zoomIn:  () => { const cy = cyRef.current; if (cy) cy.zoom({ level: cy.zoom() * 1.25, renderedPosition: { x: cy.width()/2, y: cy.height()/2 } }) },
      zoomOut: () => { const cy = cyRef.current; if (cy) cy.zoom({ level: cy.zoom() * 0.8,  renderedPosition: { x: cy.width()/2, y: cy.height()/2 } }) },
      fit:     () => { try { cyRef.current?.fit(undefined, FIT_PADDING) } catch {} },
    }), [])

    // build element list: one edge per (target_ip, attack_type) group
    // so two TcpSynFlood runs against the same host render as a single
    // "TcpSynFlood ×2" edge instead of two stacked vertical labels.
    // dataKey changes only when the attack graph shape changes so
    // resize doesn't force a remount
    const elements = useMemo(() => {
      const assetByIp = new Map(assets.map(a => [a.host_ip, a] as const))
      const groups = groupAttackRuns(attackRuns)
      const nodes: any[] = []
      const edges: any[] = []
      if (groups.length > 0) {
        nodes.push({ data: { id: 'attacker', type: 'Attacker', label: 'Attacker\n(surveyor)' } })
        const seenTargets = new Set<string>()
        for (const g of groups) {
          const ip = g.target_ip
          if (!seenTargets.has(ip)) {
            seenTargets.add(ip)
            const a = assetByIp.get(ip)
            const label = a?.host_name ? `${a.host_name}\n${ip}` : ip
            nodes.push({
              data: {
                id: `host:${ip}`, type: 'Host', label, ip,
                hostname: a?.host_name ?? null,
                mac: a?.mac_address ?? null,
                vendor: a?.vendor ?? null,
                subnet: a?.subnet_cidr ?? null,
                deviceType: a?.device_type ?? null,
                asset_id: a?.id ?? null,
              },
            })
          }
          // one aggregated edge per (target, attack_type). label gets a
          // ×N suffix when more than one run was bundled
          const groupLabel = g.count > 1 ? `${g.attack_type} ×${g.count}` : g.attack_type
          edges.push({
            data: {
              id:               `attack-group:${ip}::${g.attack_type}`,
              source:           'attacker',
              target:           `host:${ip}`,
              label:            groupLabel,
              // group payload, flagged so the drawer renders the
              // per-run table instead of the single-run layout
              kind:             'attack-group',
              target_ip:        ip,
              attack_type:      g.attack_type,
              count:            g.count,
              total_packets:    g.totalPackets,
              total_duration:   g.totalDuration,
              first_start_time: g.firstStartTime,
              last_stop_time:   g.lastStopTime,
              protocols:        g.protocols,
              runs:             g.runs,
            },
          })
        }
      }
      return { nodes, edges }
    }, [assets, attackRuns])

    const dataKey = `${elements.nodes.length}:${elements.edges.length}`

    useEffect(() => {
      if (!containerRef.current) return
      const container = containerRef.current
      if (elements.nodes.length === 0) {
        // no elements, destroy any prior instance so the empty-state
        // overlay renders against a clean container
        if (cyRef.current) { try { cyRef.current.destroy() } catch {} ; cyRef.current = null }
        return
      }
      const cy = cytoscape({
        container,
        elements: [
          ...elements.nodes.map(n => ({ group: 'nodes' as const, data: n.data })),
          ...elements.edges.map(e => ({ group: 'edges' as const, data: e.data })),
        ],
        style: styles,
        wheelSensitivity: 0.6,
        minZoom: 0.1,
        maxZoom: 3,
      })
      cyRef.current = cy
      try {
        // breadthfirst + horizontal=true places the Attacker on the left
        // and stacks targets vertically to the right. with one edge per
        // target (after aggregation) labels no longer overlap
        cy.layout({
          name: 'breadthfirst',
          directed: true,
          padding: FIT_PADDING,
          spacingFactor: 1.5,
          fit: true,
          animate: false,
          // @ts-expect-error - cytoscape types miss the optional flag
          horizontal: true,
        }).run()
      } catch { /* layout is best-effort */ }
      cy.on('tap', 'node', evt => {
        const data = evt.target.data()
        if (data.type === 'Attacker') {
          onSelect({ type: 'Attacker', label: 'Attacker (surveyor)' })
        } else {
          onSelect(data)
        }
      })
      cy.on('tap', 'edge', evt => {
        const data = evt.target.data()
        // preserve the kind already on the edge data ('attack-group'
        // for the aggregated edges built above). the drawer dispatches
        // on `kind` to choose the right render path
        onSelect({ ...data, kind: data.kind ?? 'attack' })
      })
      cy.on('tap', evt => { if (evt.target === cy) onSelect(null) })

      const ro = new ResizeObserver(() => {
        try { cy.resize() } catch {}
        try { cy.fit(undefined, FIT_PADDING) } catch {}
      })
      ro.observe(container)
      return () => {
        ro.disconnect()
        try { cy.destroy() } catch {}
        cyRef.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataKey])

    const isEmpty = elements.nodes.length === 0
    return (
      <div className="relative w-full h-full">
        <div
          ref={containerRef}
          className="rounded-lg border border-slate-200 bg-slate-50 w-full h-full"
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white/90 border border-slate-200 rounded-lg px-4 py-3 text-center">
              <div className="text-sm font-medium text-slate-700 mb-0.5">No attack runs</div>
              <div className="text-xs text-slate-500">This engagement didn't capture any attack runs.</div>
            </div>
          </div>
        )}
      </div>
    )
  }
)
AttackPathCanvas.displayName = 'AttackPathCanvas'
